import React, { useState, useEffect, useMemo } from 'react';
import DataTable from '../components/common/DataTable';
import { DataForm } from '../components/common/DataForm';
import SearchBar from '../components/common/SearchBar';
import PageHeader from '../components/common/PageHeader';
import Pagination from '../components/common/Pagination';
import DeleteConfirmModal from '../components/common/DeleteConfirmModal';
import useDebouncedSearch from '../hooks/useDebouncedSearch';
import { Hash, Trophy, Link as LinkIcon } from 'lucide-react';
import toast from 'react-hot-toast';

const MOCK_DATA = [
  { id: 1, name: 'President Cup India 2026', t_url: 'https://itcsf.org/president-cup-2026', image: '/img/aff-1.png', status: 'Upcoming' },
  { id: 2, name: 'Big Bash Dhamaka 2026', t_url: 'https://itcsf.org/big-bash-2026', image: '/img/aff-2.png', status: 'Ongoing' }
];

const COLUMNS = [
  { header: 'Tournament Name', accessor: 'name' },
  { header: 'Tournament URL', accessor: 't_url' },
  { header: 'Status', accessor: 'status' }
];

const FORM_FIELDS = [
  { label: 'Tournament Banner', accessor: 'image', type: 'file', fullWidth: true },
  { label: 'Tournament Name', accessor: 'name', placeholder: 'e.g. President Cup 2026' },
  { label: 'Tournament Url', accessor: 't_url', placeholder: 'Enter Tournament url' },
  { label: 'Status', accessor: 'status', type: 'select', options: [
    { label: 'Upcoming', value: 'Upcoming' },
    { label: 'Ongoing', value: 'Ongoing' },
    { label: 'Completed', value: 'Completed' }
  ]}
];

export default function Tournaments() {
  const [dataList, setDataList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingData, setEditingData] = useState(null);
  
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Search & Pagination State
  const [statusFilter, setStatusFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const { searchQuery, setSearchQuery, debouncedQuery } = useDebouncedSearch(() => setCurrentPage(1));
  const itemsPerPage = 5;

  const fetchData = async () => {
    setIsLoading(true);
    setTimeout(() => {
      setDataList(MOCK_DATA);
      setIsLoading(false);
    }, 500);
  };

  useEffect(() => {
    fetchData();
  }, []);



  const filteredData = useMemo(() => {
    let result = dataList;
    if (statusFilter !== 'All') {
      result = result.filter(item => item.status === statusFilter);
    }
    if (debouncedQuery) {
      result = result.filter(item => 
        Object.values(item).some(val => 
          String(val).toLowerCase().includes(debouncedQuery.toLowerCase())
        )
      );
    }
    return result;
  }, [dataList, debouncedQuery, statusFilter]);

  const kpis = useMemo(() => {
    const total = dataList.length;
    return [
      { label: "Total Tournaments", value: total, icon: <Trophy size={20} />, color: 'blue' },
      { label: "Upcoming Matches", value: dataList.filter(d => d.status === 'Upcoming').length, icon: <LinkIcon size={20} />, color: 'orange' },
      { label: "Ongoing Matches", value: dataList.filter(d => d.image).length, icon: <Hash size={20} />, color: 'green' }
    ];
  }, [dataList]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleFormSubmit = async (formData) => {
    const processedData = {
      ...formData,
      status: formData.status || 'Upcoming'
    };
    if (editingData) {
      setDataList(prev => prev.map(item => item.id === editingData.id ? { ...item, ...processedData } : item));
      toast.success('Tournament updated successfully!');
    } else {
      const newItem = { ...processedData, id: Date.now() };
      setDataList(prev => [newItem, ...prev]);
      console.log("newItem",newItem);
      toast.success('Tournament added successfully!');
    }
    setIsFormOpen(false);
    setEditingData(null);
  };

  const handleEdit = (row) => {
    setEditingData(row);
    setIsFormOpen(true);
  };

  const handleDeleteClick = (row) => {
    setDeleteTarget(row);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteExecute = async () => {
    if (!deleteTarget) return;
    setIsDeleteModalOpen(false);
    setDataList(prev => prev.filter(item => item.id !== deleteTarget.id));
    toast.success('Tournament deleted successfully!');
    setDeleteTarget(null);
  };

  return (
    <div className="page-content">
      <PageHeader 
        title="Affiliated Tournaments"
        subtitle="Manage external tournament links, status, and custom banners"
        onAdd={() => { setEditingData(null); setIsFormOpen(true); }}
        addLabel="Add Tournament"
        kpis={kpis}
      />
      
      {isFormOpen && (
        <DataForm 
          title={editingData ? `Edit Tournament` : `Add New Tournament`} 
          fields={FORM_FIELDS} 
          initialData={editingData}
          onSubmit={handleFormSubmit}
          onClose={() => setIsFormOpen(false)}
        />
      )}
      
      <div className="table-card-container">
        <div className="table-actions-area">
          <div className="table-actions-left">
            <SearchBar 
              placeholder="Search tournaments..."
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </div>

          <div className="table-actions-right">
            <div className="filter-select-wrapper">
              <select 
                className="table-filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="All">All Statuses</option>
                <option value="Upcoming">Upcoming</option>
                <option value="Ongoing">Ongoing</option>
                <option value="Completed">Completed</option>
              </select>
            </div>
          </div>
        </div>

        <div className="table-responsive-wrapper">
          <DataTable 
            columns={COLUMNS} 
            data={paginatedData} 
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
            loading={isLoading}
          />
        </div>

        <Pagination 
          currentPage={currentPage}
          totalPages={totalPages}
          totalEntries={filteredData.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setDeleteTarget(null); }}
        onConfirm={handleDeleteExecute}
        title="Delete Tournament"
        message="Are you sure you want to delete this tournament profile?"
        itemName={deleteTarget ? deleteTarget.name : ''}
      />
    </div>
  );
}
